package com.flowyride.core.domain.navigation

import com.flowyride.core.domain.analysis.bearingTo
import com.flowyride.core.domain.analysis.distanceTo
import com.flowyride.core.domain.model.GeoPoint
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow

/**
 * Fixes of a rider following [path] at a steady speed: tests, and trying the navigation at a desk.
 *
 * @param speedMetersPerSecond riding speed (default 70 km/h).
 * @param intervalMillis time between fixes.
 */
class RideSimulator(
    path: List<GeoPoint>,
    private val speedMetersPerSecond: Double = 70.0 / 3.6,
    private val intervalMillis: Long = 1_000,
) {
    private val geometry = TrackGeometry(path)

    /** One fix per interval from the start to the end of the path, timestamps from [startMillis]. */
    fun fixes(startMillis: Long = 0): Sequence<LocationFix> = sequence {
        val step = speedMetersPerSecond * intervalMillis / 1_000.0
        var distance = 0.0
        var time = startMillis
        while (true) {
            val point = geometry.pointAt(distance)
            val ahead = geometry.pointAt(distance + step)
            val bearing = if (point.distanceTo(ahead) > 0.5) point.bearingTo(ahead) else geometry.bearingAt(distance)
            yield(LocationFix(point, time, bearing = bearing, speedMetersPerSecond = speedMetersPerSecond, accuracyMeters = 5.0))
            if (distance >= geometry.length) break
            distance = (distance + step).coerceAtMost(geometry.length)
            time += intervalMillis
        }
    }

    /** The same fixes in real time, as a [LocationSource] would give them. */
    fun source(): LocationSource = object : LocationSource {
        override fun fixes(): Flow<LocationFix> = flow {
            var first = true
            for (fix in this@RideSimulator.fixes(startMillis = 0)) {
                if (!first) delay(intervalMillis)
                first = false
                emit(fix)
            }
        }
    }
}
